import { z } from "zod/v4";

import { AssetType, AssetTypeSchema } from "../asset-type.types";
import { BaseRequestArgs } from "../base-reader";

export interface MarketTradesRequestArgs extends BaseRequestArgs {
  marketName: string;
  /**
   * Product the market name belongs to (default "perp"). Perp and spot
   * derive different addresses for the same name.
   */
  assetType?: AssetType;
  limit?: number;
}

export interface MarketTradesByAddrRequestArgs extends BaseRequestArgs {
  marketAddr: string;
  limit?: number;
}

export interface MarketTradesWindowArgs extends BaseRequestArgs {
  marketAddr: string;
  /** At most 31 days apart; the API rejects a wider window. */
  fromMs: number;
  toMs: number;
  minNotionalUsd?: number;
  /** Default 500, max 1000. */
  limit?: number;
}

export const MarketTradeSchema = z.object({
  /** Absent on API versions that predate spot support (treat as "perp"). */
  asset_type: AssetTypeSchema.optional(),
  account: z.string(),
  market: z.string(),
  /** Perp: position-centric (OpenLong/...). Spot: side ("Buy" / "Sell"). */
  action: z.string(),
  size: z.number(),
  price: z.number(),
  is_profit: z.boolean(),
  realized_pnl_amount: z.number(),
  realized_funding_amount: z.number(),
  is_rebate: z.boolean(),
  fee_amount: z.number(),
  /** Spot only: FA address `fee_amount` is denominated in; absent on perp. */
  fee_asset: z.string().optional(),
  transaction_unix_ms: z.number(),
  transaction_version: z.number(),
});

export const MarketTradesHistorySchema = z.object({
  items: z.array(MarketTradeSchema),
  total_count: z.number(),
});

export const MarketTradesWindowSchema = z.array(MarketTradeSchema);

export const MarketTradeWsMessageSchema = z.object({
  trades: z.array(MarketTradeSchema),
});

export type MarketTrade = z.infer<typeof MarketTradeSchema>;
export type MarketTradesHistory = z.infer<typeof MarketTradesHistorySchema>;
export type MarketTradeWsMessage = z.infer<typeof MarketTradeWsMessageSchema>;
